export interface OrderItemIdentity {
  symbol: string
  itemType: 'instrument' | 'option'
  accountId?: string
}

export function isSameOrderItem(a: OrderItemIdentity, b: OrderItemIdentity): boolean {
  return (
    a.symbol === b.symbol &&
    a.itemType === b.itemType &&
    (a.accountId ?? '') === (b.accountId ?? '')
  )
}

/** Same visible row even when its account was retargeted. */
export function isSameVisualOrderItem(a: OrderItemIdentity, b: OrderItemIdentity): boolean {
  return a.symbol === b.symbol && a.itemType === b.itemType
}

/**
 * Reconciles the selection stores back into the form without letting their
 * option/instrument grouping reorder rows already visible to the user.
 */
export function preserveCurrentItemOrder<T extends OrderItemIdentity>(
  currentItems: T[],
  newItems: T[]
): T[] {
  const used = new Set<number>()
  const ordered: (T | null)[] = currentItems.map(() => null)

  const takeAt = (index: number): T | null => {
    if (used.has(index) || index >= newItems.length) return null
    used.add(index)
    return newItems[index]
  }

  const takeMatch = (predicate: (item: T) => boolean): T | null => {
    const index = newItems.findIndex((item, i) => !used.has(i) && predicate(item))
    if (index === -1) return null
    used.add(index)
    return newItems[index]
  }

  // Reserve unchanged rows first, especially when a different row was deleted.
  currentItems.forEach((currentItem, currentIdx) => {
    ordered[currentIdx] = takeMatch((item) => isSameOrderItem(item, currentItem))
  })

  // An account switch changes submission identity, but not the user's visible row.
  currentItems.forEach((currentItem, currentIdx) => {
    if (ordered[currentIdx]) return
    ordered[currentIdx] = takeMatch((item) => isSameVisualOrderItem(item, currentItem))
  })

  currentItems.forEach((_currentItem, currentIdx) => {
    if (ordered[currentIdx]) return
    ordered[currentIdx] = takeAt(currentIdx) ?? takeMatch(() => true)
  })

  const compactOrdered = ordered.filter((item): item is T => Boolean(item))
  for (let i = 0; i < newItems.length; i++) {
    if (!used.has(i)) compactOrdered.push(newItems[i])
  }

  return compactOrdered
}
